import { FALSE, NULL, TRUE } from "constants/primitives";
import { consentLogger } from "helpers/logger";
import { type Reducer } from "preact/hooks";
import {
  TCFConsentStateActionObject,
  ITCFConsentState,
  ITCFConsentStateInput,
  TCF_CONSENT_STATE_ACTION_CONSENT_SAVE_FINISHED,
  TCF_CONSENT_STATE_ACTION_START_CONSENT_SAVE,
  TCF_CONSENT_STATE_ACTION_TOGGLE_CATEGORY,
  TCF_CONSENT_STATE_ACTION_TOGGLE_VENDOR,
  TCF_CONSENT_STATE_ACTION_ACCEPT_ALL,
  TCF_CONSENT_STATE_ACTION_LOG_TO_API,
  TCF_CONSENT_STATE_ACTION_DENY,
  TCF_CONSENT_STATE_ACTION_TOGGLE_GATP_VENDOR,
  TCF_CONSENT_STATE_ACTION_TOGGLE_VENDOR_LEGITIMATE_INTEREST,
  TCF_CONSENT_STATE_ACTION_TOGGLE_CATEGORY_LEGITIMATE_INTEREST,
  TCF_CONSENT_STATE_ACTION_ACCEPT_ALL_LEGITIMATE_INTEREST,
  TCFCategoryTypes,
  ITCFConsent,
  TCFEventStatus,
  TCF_CONSENT_STATE_ACTION_SET_CONSENT,
} from "./types";
import {
  TCF_PURPOSES_DISABLED_LEGITIMATE_INTEREST,
  TCF_VENDOR_CATEGORIES_TYPES,
} from "../constantsTCF";
import isTCFConsentEqual from "../tcfHelpers/isTCFConsentEqual";
import getTimestamp from "helpers/getTimestamp";
import TCFsaveLocalTCString from "../TCFsaveLocalTCString";
import TCFGenerateTCString from "../tcfHelpers/TCFGenerateTCString";
import { calculateTCFBaseConsent } from "../tcfHelpers/calculateTCFBaseConsent";
import TCFshouldForceReconsent from "../tcfHelpers/TCFshouldForceReconsent";
import createPromise from "helpers/createPromise";
import logUserConsent from "helpers/logUserConsent";
import report from "helpers/report";
import { ITCFLogConsentToApiEvent } from "../tcfHelpers/dispatchLogConsentoApiWithTCFEvent";
import TCFGenerateACString from "../tcfHelpers/TCFGenerateACString";

export type TCFConsentStateReducerFactory = () => Reducer<
  ITCFConsentState,
  TCFConsentStateActionObject
>;

export const createTCFConsentStateReducer: TCFConsentStateReducerFactory =
  () => {
    return (state, action) => {
      switch (action.type) {
        case TCF_CONSENT_STATE_ACTION_TOGGLE_VENDOR: {
          const { vendorID, shouldBeAccepted } = action.payload;

          consentLogger("Toggle vendor", vendorID);

          // Create temp state to check for categories that should be enabled, before state of this vendor toggle will finish
          const locTempConsent = {
            ...state.tempConsent,
            vendors: {
              ...state.tempConsent.vendors,
              [vendorID]: shouldBeAccepted,
            },
          };

          return {
            ...state,
            tempConsent: {
              ...locTempConsent,
              vendors: {
                ...locTempConsent.vendors,
                [vendorID]: shouldBeAccepted,
              },
            },
          };
        }

        case TCF_CONSENT_STATE_ACTION_TOGGLE_VENDOR_LEGITIMATE_INTEREST: {
          const { vendorID, shouldBeAccepted } = action.payload;

          consentLogger("Toggle vendor", vendorID);

          // Create temp state to check for categories that should be enabled, before state of this vendor toggle will finish
          const locTempConsent = {
            ...state.tempConsent,
            vendorsLegitimateInterest: {
              ...state.tempConsent.vendorsLegitimateInterest,
              [vendorID]: shouldBeAccepted,
            },
          };

          return {
            ...state,
            tempConsent: {
              ...locTempConsent,
              vendorsLegitimateInterest: {
                ...locTempConsent.vendorsLegitimateInterest,
                [vendorID]: shouldBeAccepted,
              },
            },
          };
        }

        case TCF_CONSENT_STATE_ACTION_TOGGLE_CATEGORY: {
          const { name, id, gvl, shouldBeAccepted } = action.payload;

          consentLogger("Toggle tcf category", { name, id }, shouldBeAccepted);
          // For the purpose of checking category connect tempState and payload of event to check for vendors that should or shouldn't be enabled/disabled as part of category
          const locTempConsent = {
            ...state.tempConsent,
            categories: {
              ...state.tempConsent.categories,
              [name]: {
                ...state.tempConsent.categories[name],
                [id]: shouldBeAccepted,
              },
            },
          };

          // Loop through vendors to check which vendors should be enabled or disabled
          state.vendorsList.forEach((vendorID) => {
            const vendorDetails = gvl.vendors[vendorID];

            // Check if vendor exist in GVL:
            if (!vendorDetails) {
              return;
            }

            // Check if vendor is part of all categories that are selected as enabled
            const allCategoriesAccepted = locTempConsent.categories;
            let shouldVendorBeAccepted = TRUE;

            // Loop through categories that have option to opt-in/out
            TCF_VENDOR_CATEGORIES_TYPES.forEach((category) => {
              // Check only categories that have switches
              if (category.hasSwitch) {
                const categoryName = category.key;
                const categoryValue = allCategoriesAccepted[categoryName];

                vendorDetails[categoryName].forEach((categoryID) => {
                  if (categoryValue) {
                    // Check if every category specified in vendor is accepted. If even one is not accepted, return false
                    if (
                      !categoryValue[categoryID as keyof typeof categoryValue]
                    ) {
                      shouldVendorBeAccepted = FALSE;
                    }
                  } else {
                    // If vendor have category but such category is empty in list of acceptedCategories, that means this vendor cant be accepted
                    shouldVendorBeAccepted = FALSE;
                  }
                });
              }
            });

            // Save information about this specific vendor to local temp consent
            locTempConsent.vendors[vendorID] = shouldVendorBeAccepted;
          });

          // Return local temp consent as temp consent to real state.
          return {
            ...state,
            tempConsent: { ...locTempConsent },
          };
        }

        case TCF_CONSENT_STATE_ACTION_TOGGLE_CATEGORY_LEGITIMATE_INTEREST: {
          const { name, id, gvl, shouldBeAccepted } = action.payload;

          consentLogger("Toggle tcf category", { name, id }, shouldBeAccepted);
          // For the purpose of checking category connect tempState and payload of event to check for vendors that should or shouldn't be enabled/disabled as part of category
          const locTempConsent = {
            ...state.tempConsent,
            categoriesLegitimateInterest: {
              ...state.tempConsent.categoriesLegitimateInterest,
              [name]: {
                ...state.tempConsent.categoriesLegitimateInterest[name],
                [id]: shouldBeAccepted,
              },
            },
          };

          // Loop through vendors to check which vendors should be enabled or disabled
          for (const vendorID of state.vendorsList) {
            const vendorDetails = gvl.vendors[vendorID];

            // Check if vendor is part of all categories that are selected as enabled
            const allCategoriesAccepted =
              locTempConsent.categoriesLegitimateInterest;
            let shouldVendorBeAccepted = shouldBeAccepted;

            // Loop through categories that have option to opt-in/out
            TCF_VENDOR_CATEGORIES_TYPES.forEach((category) => {
              // Check only categories that have switches and is not in list of legitimate interest that are disabled
              if (
                category.hasSwitch &&
                category.hasLegitimateInterest &&
                category.legIntName
              ) {
                const categoryName = category.key;
                const categoryLegIntName = category.legIntName;
                const categoryValue = allCategoriesAccepted[categoryName];

                for (const categoryID of vendorDetails[categoryLegIntName]) {
                  // Check if category is one of those that can't have legitimate interest
                  if (
                    categoryName === TCFCategoryTypes.PURPOSES &&
                    TCF_PURPOSES_DISABLED_LEGITIMATE_INTEREST.includes(
                      categoryID
                    )
                  ) {
                    return FALSE;
                  }

                  if (categoryValue) {
                    // Check if every category specified in vendor is accepted. If even one is not accepted, return false
                    if (
                      !categoryValue[categoryID as keyof typeof categoryValue]
                    ) {
                      shouldVendorBeAccepted = FALSE;
                    }
                  } else {
                    // If vendor have category but such category is empty in list of acceptedCategories, that means this vendor cant be accepted
                    shouldVendorBeAccepted = FALSE;
                  }
                }
              }
            });

            // Save information about this specific vendor to local temp consent
            locTempConsent.vendorsLegitimateInterest[vendorID] =
              shouldVendorBeAccepted;
          }

          // Return local temp consent as temp consent to real state.
          return {
            ...state,
            tempConsent: { ...locTempConsent },
          };
        }

        case TCF_CONSENT_STATE_ACTION_START_CONSENT_SAVE: {
          // copy the consent object to avoid modifying passed object
          let locTempState: ITCFConsentState = { ...state };
          const { gvl, userLang, widgetConfig, forceFakeSave } = action.payload;

          // `forceFakeSave` is variable in case if we want banner to act as user saved but actually not. Needed for scanner running through page and when "hide outside eu" option is enabled

          if (!forceFakeSave) {
            const hasPreviouslyConsented: boolean = locTempState.hasConsented;

            const isConsentEqualToPrevious: boolean = isTCFConsentEqual(
              locTempState.tempConsent,
              state.userConsent,
              locTempState.vendorsList
            );

            // don't perform any action if consent is the same as before
            // and we aren't forcing re-consent
            if (
              hasPreviouslyConsented &&
              isConsentEqualToPrevious &&
              !state.isForcingReconsent
            ) {
              return { ...state, shouldBeLogged: FALSE };
            }
          }

          // Update state with tempConsent and information that user has consented
          const timestamp = getTimestamp();
          locTempState = {
            ...locTempState,
            consentTimestamp: timestamp,
            userConsent: { ...locTempState.tempConsent },
            hasConsented: TRUE,
            shouldBeLogged: TRUE,
          };

          // Generate tc string to be saved
          const TCString = TCFGenerateTCString(gvl, locTempState, userLang);

          let ACString = "";

          // Generate acstring if such option is enabled
          if (widgetConfig.acEnabled) {
            ACString = TCFGenerateACString(
              locTempState.userConsent.gatp,
              widgetConfig.acVendors
            );
          }

          if (!forceFakeSave) {
            // Save TC string to both cookie and local storage
            TCFsaveLocalTCString(TCString, widgetConfig, ACString);
          }

          // Update eventStatus
          locTempState.eventStatus = TCFEventStatus.INTERACTION_COMPLETE;

          return {
            ...locTempState,
          };
        }

        case TCF_CONSENT_STATE_ACTION_CONSENT_SAVE_FINISHED: {
          return {
            ...state,
          };
        }

        case TCF_CONSENT_STATE_ACTION_ACCEPT_ALL: {
          const { gvl } = action.payload;
          const newConsent: ITCFConsent = calculateTCFBaseConsent();

          // Loop through all categories and enable them
          TCF_VENDOR_CATEGORIES_TYPES.forEach((category) => {
            // Check only categories that have switches
            if (category.hasSwitch) {
              const categoryName = category.key;

              Object.entries(gvl[categoryName]).forEach(([, value]) => {
                newConsent.categories[categoryName] = {
                  ...newConsent.categories[categoryName],
                  [value.id]: TRUE,
                };
                if (
                  category.hasLegitimateInterest &&
                  !TCF_PURPOSES_DISABLED_LEGITIMATE_INTEREST.includes(value.id)
                ) {
                  let shouldLegitimateBeAccepted = TRUE;

                  // Check if category is one of those that can't have legitimate interest
                  if (
                    categoryName === TCFCategoryTypes.PURPOSES &&
                    TCF_PURPOSES_DISABLED_LEGITIMATE_INTEREST.includes(value.id)
                  ) {
                    shouldLegitimateBeAccepted = FALSE;
                  }

                  newConsent.categoriesLegitimateInterest[categoryName] = {
                    ...newConsent.categoriesLegitimateInterest[categoryName],
                    [value.id]: shouldLegitimateBeAccepted,
                  };
                }
              });
            }
          });

          // loop through all vendors to enable them
          state.vendorsList.forEach((vendorID) => {
            // Check if vendor exist in GVL:
            if (!gvl.vendors[vendorID]) {
              return;
            }

            Object.assign(newConsent.vendors, { [vendorID]: TRUE });

            // Enable legitimate interest for vendor only if such vendor require any of the legitimate interets
            if (
              Object.values(gvl.vendors).find(
                (vendor) =>
                  vendor.id === vendorID && vendor.legIntPurposes.length > 0
              )
            ) {
              Object.assign(newConsent.vendorsLegitimateInterest, {
                [vendorID]: TRUE,
              });
            }
          });

          // Loop through gatp to enable them
          state.gatpList.forEach((vendorID) => {
            Object.assign(newConsent.gatp, { [vendorID]: TRUE });
          });

          return { ...state, tempConsent: newConsent };
        }

        case TCF_CONSENT_STATE_ACTION_ACCEPT_ALL_LEGITIMATE_INTEREST: {
          const { gvl } = action.payload;
          const locTempConsent: ITCFConsent = { ...state.tempConsent };

          // Loop through all categories and enable them
          TCF_VENDOR_CATEGORIES_TYPES.forEach((category) => {
            // Check only categories that have switches
            if (category.hasSwitch) {
              const categoryName = category.key;

              Object.entries(gvl[categoryName]).forEach(([, value]) => {
                if (category.hasLegitimateInterest) {
                  let shouldBeAccepted = TRUE;

                  // Check if category is one of those that can't have legitimate interest
                  if (
                    categoryName === TCFCategoryTypes.PURPOSES &&
                    TCF_PURPOSES_DISABLED_LEGITIMATE_INTEREST.includes(value.id)
                  ) {
                    shouldBeAccepted = FALSE;
                  }

                  locTempConsent.categoriesLegitimateInterest[categoryName] = {
                    ...locTempConsent.categoriesLegitimateInterest[
                      categoryName
                    ],
                    [value.id]: shouldBeAccepted,
                  };
                }
              });
            }
          });

          // loop through all vendors to enable them
          state.vendorsList.forEach((vendorID) => {
            // Check if vendor exist in GVL:
            if (!gvl.vendors[vendorID]) {
              return;
            }

            // Enable legitimate interest for vendor only if such vendor require any of the legitimate interets
            if (
              Object.values(gvl.vendors).find(
                (vendor) =>
                  vendor.id === vendorID && vendor.legIntPurposes.length > 0
              )
            ) {
              Object.assign(locTempConsent.vendorsLegitimateInterest, {
                [vendorID]: TRUE,
              });
            }
          });

          return { ...state, tempConsent: locTempConsent };
        }

        case TCF_CONSENT_STATE_ACTION_LOG_TO_API: {
          // {gvl, consentPayload} = action.payload;
          const { gvl, consentInfo, userLang, setVisitorId, widgetConfig } =
            action.payload;
          /**
           * Connect information from normal consent and tcf consent in order to save both to the database together
           */
          const logConsentToApi = ({
            consent,
            userAction,
            visitorId,
            configVersion,
            visitorCountry,
            visitorRegion,
            consentPolicy,
            granularMetadata,
            hasNormalConsentChanged,
          }: ITCFLogConsentToApiEvent): Promise<void> => {
            return createPromise<void>((resolve) => {
              const tcstring = TCFGenerateTCString(gvl, state, userLang);
              const acstring = TCFGenerateACString(
                state.userConsent.gatp,
                widgetConfig.acVendors
              );

              // Check if TCF consent changed
              if (!hasNormalConsentChanged && !state.shouldBeLogged) {
                return FALSE;
              }

              logUserConsent(
                consent,
                userAction,
                visitorId,
                configVersion,
                visitorCountry,
                visitorRegion,
                consentPolicy,
                granularMetadata,
                tcstring,
                acstring
              )
                .then((newVisitorId) => {
                  if (newVisitorId) {
                    setVisitorId(newVisitorId);
                  }
                }, report)
                .finally(resolve);
            });
          };

          logConsentToApi(consentInfo);

          return {
            ...state,
          };
        }

        case TCF_CONSENT_STATE_ACTION_DENY: {
          const { gvl } = action.payload;
          const newConsent: ITCFConsent = calculateTCFBaseConsent();

          // Loop through all categories and disable them
          TCF_VENDOR_CATEGORIES_TYPES.forEach((category) => {
            // Check only categories that have switches
            if (category.hasSwitch) {
              const categoryName = category.key;

              Object.entries(gvl[categoryName]).forEach(([, value]) => {
                newConsent.categories[categoryName] = {
                  ...newConsent.categories[categoryName],
                  [value.id]: FALSE,
                };

                if (category.hasLegitimateInterest) {
                  newConsent.categoriesLegitimateInterest[categoryName] = {
                    ...newConsent.categoriesLegitimateInterest[categoryName],
                    [value.id]: FALSE,
                  };
                }
              });
            }
          });

          // loop through all vendors to disable them
          state.vendorsList.forEach((vendorID) => {
            // Check if vendor exist in GVL:
            if (!gvl.vendors[vendorID]) {
              return;
            }

            Object.assign(newConsent.vendors, { [vendorID]: FALSE });
            Object.assign(newConsent.vendorsLegitimateInterest, {
              [vendorID]: FALSE,
            });
          });

          // Loop through gatp to disable them
          state.gatpList.forEach((vendorID) => {
            Object.assign(newConsent.gatp, { [vendorID]: FALSE });
          });

          return { ...state, tempConsent: newConsent };
        }

        case TCF_CONSENT_STATE_ACTION_TOGGLE_GATP_VENDOR: {
          const { vendorID, shouldBeAccepted } = action.payload;

          consentLogger("Toggle vendor", vendorID);

          return {
            ...state,
            tempConsent: {
              ...state.tempConsent,
              gatp: { ...state.tempConsent.gatp, [vendorID]: shouldBeAccepted },
            },
          };
        }

        case TCF_CONSENT_STATE_ACTION_SET_CONSENT: {
          const { consent } = action.payload;

          consentLogger("Set consent");

          return {
            ...state,
            tempConsent: consent,
          };
        }

        default:
          return state;
      }
    };
  };

export const calculateTCFInitialState = (input: ITCFConsentStateInput) => {
  const { prevConsent, prevConsentTimestamp, widgetConfig } = input;

  const state: ITCFConsentState = {
    consentTimestamp: null,
    userConsent: calculateTCFBaseConsent(),
    baseConsent: calculateTCFBaseConsent(),
    tempConsent: calculateTCFBaseConsent(),
    hasConsented: FALSE,
    vendorsList: [],
    gatpList: [],
    tcfCountry: "",
    isForcingReconsent: FALSE,
    shouldBeLogged: FALSE,
    eventStatus: TCFEventStatus.IS_SHOWN,
  };

  // base consent - pre-selected services/categories
  state.baseConsent = calculateTCFBaseConsent();

  if (prevConsent) {
    state.userConsent = { ...prevConsent };
    state.tempConsent = { ...prevConsent };
    state.hasConsented = TRUE;
    state.eventStatus = TCFEventStatus.TCLOADED;
  } else {
    state.userConsent = calculateTCFBaseConsent();
    state.tempConsent = calculateTCFBaseConsent();
    state.hasConsented = FALSE;
  }

  state.consentTimestamp = prevConsentTimestamp || NULL;

  state.vendorsList = input.vendorsList;
  state.gatpList = input.gatpList;
  state.tcfCountry = input.tcfCountry;

  if (
    TCFshouldForceReconsent(prevConsent, prevConsentTimestamp, widgetConfig)
  ) {
    consentLogger("forcing reconsent");

    state.isForcingReconsent = TRUE;
  }

  return state;
};
